import { arraysEqual } from "../../utils/helpers";

const occurrenceTimes = (occ) => ({
  doors_time: occ.doors_time || null,
  // A blanked set time is a removed one.
  set_times: (occ.set_times || [])
    .filter((t) => t.time)
    .map(({ artist_id, time }) => ({ artist_id, time })),
});

// The doors and set times an edit changed, and only those (#792): an unchanged key is left out, so the
// API keeps what is stored now — a time research accepted while the editor was open survives the save.
// null / [] clears.
//
// A time edit also reaches the show's other tiers that held the old value (`follow_show_time_edits`);
// an occurrence whose "apply to every tier" box was unticked says so with `times_apply_to_show: false`.
export const changedOccurrenceTimes = (occ, loaded) => {
  const before = occurrenceTimes(loaded || {});
  const changed = Object.fromEntries(
    Object.entries(occurrenceTimes(occ)).filter(
      ([key, value]) => !arraysEqual(value, before[key]),
    ),
  );
  if (Object.keys(changed).length > 0 && occ.timesApplyToShow === false)
    changed.times_apply_to_show = false;
  return changed;
};

// One show: its date and start to the minute, as the API keys a show's ticket tiers (`show_key`) — a
// no-specific-time listing is timeless whatever placeholder time it carries.
const showKey = (occ) => {
  if (!occ.local_date) return null;
  const start = occ.no_specific_time ? "" : (occ.local_time || "").slice(0, 5);
  return `${occ.local_date}|${start}`;
};

// Whether another occurrence of the list is a ticket tier of the same show — the only case where a
// time edit can reach more than the occurrence edited, so the only one that offers the choice.
export const hasShowSiblings = (occ, occurrences) =>
  showKey(occ) !== null &&
  (occurrences || []).some(
    (other) => other !== occ && showKey(other) === showKey(occ),
  );

/** Whether a date is cancelled, however a provider spelt it (the API compares lowercased too). */
export const isCancelled = (occ) =>
  String(occ.status || "").toLowerCase() === "cancelled";

/**
 * Whether a date's cancellation survives the provider re-listing it on sale, as the editor starts:
 * a date we cancelled (`cancelled_by`) is kept, a provider's is not until ticked, and any other date
 * starts ticked, so cancelling it in this edit keeps it.
 */
export const initialKeepCancelled = (occ) =>
  isCancelled(occ) ? Boolean(occ.cancelled_by) : true;

/**
 * The `keep_cancelled` save instruction a date sends: only on a cancelled date. Built the same way
 * for the form and the loaded event, so a form nobody touched compares equal and sends nothing.
 */
export const keepCancelledPayload = (occ, keep) =>
  isCancelled(occ) && keep !== undefined ? { keep_cancelled: keep } : {};

/**
 * A date with its status changed. Cancelling it in the editor ticks Keep cancelled, whatever the box
 * said before (a provider's cancellation the admin set On Sale and back): the admin cancelled it.
 */
export const withStatus = (occ, status) => ({
  ...occ,
  status,
  ...(isCancelled({ status }) && !isCancelled(occ)
    ? { keep_cancelled: true }
    : {}),
});

/**
 * Who cancelled a date, for the event detail: ours (`cancelled_by`, kept through provider merges) or
 * the provider's (follows the provider). Null on a date that is not cancelled.
 */
export const cancellationSource = (occ) => {
  if (!isCancelled(occ)) return null;
  if (occ.cancelled_by === "research")
    return { label: "Kept · research", kept: true };
  if (occ.cancelled_by) return { label: "Kept · admin", kept: true };
  return { label: "Provider's", kept: false };
};
